/**
 * Mappers between Supabase rows (snake_case, JSONB blobs) and our
 * domain types (camelCase, nested arrays).
 *
 * Why: the database schema uses Postgres conventions (snake_case columns,
 * date-only types, JSONB for nested data) while the React app uses
 * TypeScript domain types in camelCase. Mappers are the single boundary
 * where shape conversion happens — everything else in the app deals only
 * in domain types.
 *
 * Each `fromXxxRow(row)` reads a raw row and produces a typed domain object.
 * Each `toXxxRow(obj)` does the reverse for inserts/updates.
 */

import type {
  CrApproval,
  GoLivePlan,
  Project,
  Task,
  Risk,
  Issue,
  ChangeRequest,
  Decision,
  ActionItem,
  Baseline,
  TeamMember,
  ProjectPhase,
  BudgetLine,
  BudgetVersion,
  BudgetLinePeriod,
} from '@/types';

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

/** Raw row shape returned from supabase.from('projects').select(*) */
export interface ProjectRow {
  id: string;
  organization_id: string;
  portfolio_id: string | null;
  program_id: string | null;
  name: string;
  description: string | null;
  scope: string | null;
  objectives: unknown;
  status: string;
  health: string;
  priority: string;
  methodology: string;
  start_date: string | null;
  end_date: string | null;
  budget: number;
  spent: number;
  completion: number;
  cpi: number | null;
  spi: number | null;
  sponsor: string | null;
  team_size: number | null;
  team: unknown;
  phases: unknown;
  active_baseline_id: string | null;
  /** Phase 30.12: pointer to current budget version. NULL when project has no budget data. */
  current_budget_version_id: string | null;
  /** Phase 30.22: per-area RAG KPIs (null/red/yellow/green) */
  kpi_scope: string | null;
  kpi_cost: string | null;
  kpi_schedule: string | null;
  kpi_overall: string | null;
  kpi_notes: string | null;
  kpis_updated_at: string | null;
  /** Phase 30.23: pointer to workspace_phases.id (soft FK; nullable) */
  current_phase_id: string | null;
  size_tier_id: string | null;
  /** Phase 34d: C-level status dashboard fields (free-text, per project) */
  executive_summary?: string | null;
  next_steps?: string | null;
  decisions_made?: string | null;
  leadership_decisions?: string | null;
  /** Migration 0076: optional go-live plan JSONB blob. */
  go_live_plan?: unknown;
  created_at: string;
  updated_at: string;
}

/**
 * Convert a project row into a domain Project. Note: this returns a project
 * with empty wbsTasks/risks/issues/changes/baselines arrays — those come
 * from separate queries and are merged in the store.
 */
export function fromProjectRow(row: ProjectRow): Project {
  return {
    id: row.id,
    name: row.name,
    description: row.description ?? '',
    scope: row.scope ?? '',
    objectives: Array.isArray(row.objectives) ? (row.objectives as string[]) : [],
    status: row.status as Project['status'],
    health: row.health as Project['health'],
    priority: row.priority as Project['priority'],
    methodology: row.methodology as Project['methodology'],
    portfolioId: row.portfolio_id,
    programId: row.program_id,
    startDate: row.start_date ?? '',
    endDate: row.end_date ?? '',
    budget: Number(row.budget),
    spent: Number(row.spent),
    completion: Number(row.completion),
    cpi: row.cpi !== null ? Number(row.cpi) : undefined,
    spi: row.spi !== null ? Number(row.spi) : undefined,
    pm: '', // pm field not in DB — will need a future migration if we want it; leave empty
    sponsor: row.sponsor ?? undefined,
    team: Array.isArray(row.team) ? (row.team as TeamMember[]) : [],
    teamSize: row.team_size ?? undefined,
    phases: Array.isArray(row.phases) ? (row.phases as ProjectPhase[]) : [],
    wbsTasks: [],
    risks: [],
    issues: [],
    changes: [],
    baselines: [],
    activeBaselineId: row.active_baseline_id,
    budgetVersions: [],
    currentBudgetVersionId: row.current_budget_version_id ?? null,
    // Phase 30.22: read KPI columns (null → undefined for cleaner type semantics)
    kpiScope: (row.kpi_scope as Project['kpiScope']) ?? undefined,
    kpiCost: (row.kpi_cost as Project['kpiCost']) ?? undefined,
    kpiSchedule: (row.kpi_schedule as Project['kpiSchedule']) ?? undefined,
    kpiOverall: (row.kpi_overall as Project['kpiOverall']) ?? undefined,
    kpiNotes: row.kpi_notes ?? undefined,
    kpisUpdatedAt: row.kpis_updated_at ?? undefined,
    // Phase 30.23: current lifecycle phase pointer
    currentPhaseId: row.current_phase_id ?? null,
    sizeTierId: row.size_tier_id ?? null,
    // Phase 34d: exec dashboard fields
    executiveSummary: row.executive_summary ?? undefined,
    nextSteps: row.next_steps ?? undefined,
    decisionsMade: row.decisions_made ?? undefined,
    leadershipDecisions: row.leadership_decisions ?? undefined,
    goLivePlan:
      row.go_live_plan && typeof row.go_live_plan === 'object' && !Array.isArray(row.go_live_plan)
        ? (row.go_live_plan as GoLivePlan)
        : undefined,
    createdDate: row.created_at,
    modifiedDate: row.updated_at,
  };
}

/**
 * Convert a domain Project (or partial) into a row payload.
 * Returns a partial row — only fields actually present on `p` are included.
 * organization_id must be passed separately for inserts.
 */
export function toProjectRow(
  p: Partial<Project>,
): Partial<ProjectRow> {
  const r: Partial<ProjectRow> = {};
  if (p.id !== undefined) r.id = p.id;
  if (p.name !== undefined) r.name = p.name;
  if (p.description !== undefined) r.description = p.description;
  if (p.scope !== undefined) r.scope = p.scope;
  if (p.objectives !== undefined) r.objectives = p.objectives;
  if (p.status !== undefined) r.status = p.status;
  if (p.health !== undefined) r.health = p.health;
  if (p.priority !== undefined) r.priority = p.priority;
  if (p.methodology !== undefined) r.methodology = p.methodology;
  if (p.portfolioId !== undefined) r.portfolio_id = p.portfolioId ?? null;
  if (p.programId !== undefined) r.program_id = p.programId ?? null;
  if (p.startDate !== undefined) r.start_date = p.startDate || null;
  if (p.endDate !== undefined) r.end_date = p.endDate || null;
  if (p.budget !== undefined) r.budget = p.budget;
  if (p.spent !== undefined) r.spent = p.spent;
  if (p.completion !== undefined) r.completion = p.completion;
  if (p.cpi !== undefined) r.cpi = p.cpi ?? null;
  if (p.spi !== undefined) r.spi = p.spi ?? null;
  if (p.sponsor !== undefined) r.sponsor = p.sponsor ?? null;
  if (p.teamSize !== undefined) r.team_size = p.teamSize ?? null;
  if (p.team !== undefined) r.team = p.team;
  if (p.phases !== undefined) r.phases = p.phases ?? [];
  if (p.activeBaselineId !== undefined) r.active_baseline_id = p.activeBaselineId;
  if (p.currentBudgetVersionId !== undefined)
    r.current_budget_version_id = p.currentBudgetVersionId ?? null;
  // Phase 30.22: KPI fields (undefined on partial → not written)
  if (p.kpiScope !== undefined) r.kpi_scope = p.kpiScope ?? null;
  if (p.kpiCost !== undefined) r.kpi_cost = p.kpiCost ?? null;
  if (p.kpiSchedule !== undefined) r.kpi_schedule = p.kpiSchedule ?? null;
  if (p.kpiOverall !== undefined) r.kpi_overall = p.kpiOverall ?? null;
  if (p.kpiNotes !== undefined) r.kpi_notes = p.kpiNotes ?? null;
  if (p.kpisUpdatedAt !== undefined) r.kpis_updated_at = p.kpisUpdatedAt ?? null;
  if (p.currentPhaseId !== undefined) r.current_phase_id = p.currentPhaseId ?? null;
  if (p.sizeTierId !== undefined) r.size_tier_id = p.sizeTierId ?? null;
  // Phase 34d: exec dashboard fields
  if (p.executiveSummary !== undefined) r.executive_summary = p.executiveSummary || null;
  if (p.nextSteps !== undefined) r.next_steps = p.nextSteps || null;
  if (p.decisionsMade !== undefined) r.decisions_made = p.decisionsMade || null;
  if (p.leadershipDecisions !== undefined) r.leadership_decisions = p.leadershipDecisions || null;
  if (p.goLivePlan !== undefined) r.go_live_plan = p.goLivePlan ?? null;
  return r;
}

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

export interface TaskRow {
  task_id: number;
  project_id: string;
  wbs: string | null;
  task: string;
  type: string;
  start_date: string | null;
  end_date: string | null;
  duration: number | null;
  percent_complete: number;
  predecessors: string;
  successors: string;
  resources: unknown;
  allocation: unknown;
  /** Phase 30.25: jsonb array of (uuid|null), position-aligned with resources */
  resource_user_ids: unknown;
  cost: number | null;
  lead_lag: string | null;
}

export function fromTaskRow(row: TaskRow): Task {
  return {
    taskId: row.task_id,
    wbs: row.wbs ?? '',
    task: row.task,
    type: row.type as Task['type'],
    startDate: row.start_date ?? '',
    endDate: row.end_date ?? '',
    duration: row.duration ?? 0,
    percentComplete: Number(row.percent_complete),
    predecessors: row.predecessors ?? '',
    successors: row.successors ?? '',
    resources: Array.isArray(row.resources) ? (row.resources as string[]) : [],
    allocation: Array.isArray(row.allocation) ? (row.allocation as number[]) : [],
    resourceUserIds: Array.isArray(row.resource_user_ids)
      ? (row.resource_user_ids as (string | null)[])
      : undefined,
    cost: row.cost !== null ? Number(row.cost) : undefined,
    leadLag: row.lead_lag ?? undefined,
  };
}

export function toTaskRow(projectId: string, t: Task): Omit<TaskRow, never> {
  return {
    project_id: projectId,
    task_id: t.taskId,
    wbs: t.wbs ?? null,
    task: t.task,
    type: t.type,
    start_date: t.startDate || null,
    end_date: t.endDate || null,
    duration: t.duration ?? null,
    percent_complete: t.percentComplete ?? 0,
    predecessors: t.predecessors ?? '',
    successors: t.successors ?? '',
    resources: t.resources ?? [],
    allocation: t.allocation ?? [],
    resource_user_ids: t.resourceUserIds ?? [],
    cost: t.cost ?? null,
    lead_lag: t.leadLag ?? null,
  };
}

// ---------------------------------------------------------------------------
// Risks
// ---------------------------------------------------------------------------

export interface RiskRow {
  id: string;
  project_id: string;
  title: string;
  description: string | null;
  category: string | null;
  probability: number;
  impact: string;
  exposure: number;
  status: string;
  owner: string | null;
  response_strategy: string | null;
  mitigation: string | null;
  identified_date: string | null;
  // Phase 20.1
  target_mitigation_date: string | null;
  last_reviewed_date: string | null;
  mitigation_status: string | null;
  // Phase 20.4
  materialized_at: string | null;
  materialized_into_issue_id: string | null;
  // Phase 34d
  is_reportable?: boolean | null;
}

export function fromRiskRow(row: RiskRow): Risk {
  return {
    id: row.id,
    title: row.title,
    description: row.description ?? '',
    category: row.category ?? '',
    probability: Number(row.probability),
    impact: row.impact as Risk['impact'],
    exposure: Number(row.exposure),
    status: row.status as Risk['status'],
    owner: row.owner ?? '',
    responseStrategy: (row.response_strategy ?? '') as Risk['responseStrategy'],
    mitigation: row.mitigation ?? '',
    identifiedDate: row.identified_date ?? '',
    // Phase 20.1
    targetMitigationDate: row.target_mitigation_date ?? '',
    lastReviewedDate: row.last_reviewed_date ?? '',
    mitigationStatus: (row.mitigation_status as Risk['mitigationStatus']) ?? undefined,
    // Phase 20.4
    materializedAt: row.materialized_at ?? '',
    materializedIntoIssueId: row.materialized_into_issue_id ?? '',
    // Phase 34d — default to true for legacy rows without the column
    isReportable: row.is_reportable ?? true,
  };
}

export function toRiskRow(projectId: string, r: Risk): Omit<RiskRow, never> {
  return {
    id: r.id,
    project_id: projectId,
    title: r.title,
    description: r.description ?? null,
    category: r.category ?? null,
    probability: r.probability ?? 50,
    impact: r.impact ?? 'medium',
    exposure: r.exposure ?? 0,
    status: r.status ?? 'open',
    owner: r.owner ?? null,
    response_strategy: r.responseStrategy ?? null,
    mitigation: r.mitigation ?? null,
    identified_date: r.identifiedDate || null,
    // Phase 20.1
    target_mitigation_date: r.targetMitigationDate || null,
    last_reviewed_date: r.lastReviewedDate || null,
    mitigation_status: r.mitigationStatus ?? null,
    // Phase 20.4
    materialized_at: r.materializedAt || null,
    materialized_into_issue_id: r.materializedIntoIssueId || null,
    // Phase 34d
    is_reportable: r.isReportable ?? true,
  };
}

// ---------------------------------------------------------------------------
// Issues
// ---------------------------------------------------------------------------

export interface IssueRow {
  id: string;
  project_id: string;
  title: string;
  description: string | null;
  status: string;
  severity: string;
  owner: string | null;
  raised_date: string | null;
  due_date: string | null;
  // Phase 20.4
  originating_risk_id: string | null;
  converted_at: string | null;
  converted_by: string | null;
  // Phase 34d
  is_reportable?: boolean | null;
}

export function fromIssueRow(row: IssueRow): Issue {
  return {
    id: row.id,
    title: row.title,
    description: row.description ?? '',
    status: row.status as Issue['status'],
    severity: row.severity as Issue['severity'],
    owner: row.owner ?? '',
    raisedDate: row.raised_date ?? '',
    dueDate: row.due_date ?? undefined,
    // Phase 20.4
    originatingRiskId: row.originating_risk_id ?? '',
    convertedAt: row.converted_at ?? '',
    convertedBy: row.converted_by ?? '',
    // Phase 34d — default true for legacy rows
    isReportable: row.is_reportable ?? true,
  };
}

export function toIssueRow(projectId: string, i: Issue): Omit<IssueRow, never> {
  return {
    id: i.id,
    project_id: projectId,
    title: i.title,
    description: i.description ?? null,
    status: i.status,
    severity: i.severity,
    owner: i.owner ?? null,
    raised_date: i.raisedDate || null,
    due_date: i.dueDate || null,
    // Phase 20.4
    originating_risk_id: i.originatingRiskId || null,
    converted_at: i.convertedAt || null,
    converted_by: i.convertedBy || null,
    // Phase 34d
    is_reportable: i.isReportable ?? true,
  };
}

// ---------------------------------------------------------------------------
// Change Requests
// ---------------------------------------------------------------------------

export interface ChangeRequestRow {
  id: string;
  project_id: string;
  title: string;
  type: string;
  status: string;
  requested_by: string | null;
  request_date: string | null;
  impact: string | null;
  cost_impact: number;
  schedule_impact: number;
  priority: string;
  justification: string | null;
  decision: string | null;
  /** Phase 30.10: optional link to a budget_lines row (composite FK to (project_id, id)). */
  budget_line_id: string | null;
  /** Migration 0075: re-baseline on approval. */
  rebaseline_on_approval?: boolean | null;
  scope_change?: string | null;
  approvals?: CrApproval[] | null;
}

export function fromChangeRequestRow(row: ChangeRequestRow): ChangeRequest {
  return {
    id: row.id,
    title: row.title,
    type: row.type as ChangeRequest['type'],
    status: row.status as ChangeRequest['status'],
    requestedBy: row.requested_by ?? '',
    requestDate: row.request_date ?? '',
    impact: row.impact ?? '',
    costImpact: Number(row.cost_impact),
    scheduleImpact: row.schedule_impact ?? 0,
    priority: row.priority as ChangeRequest['priority'],
    justification: row.justification ?? '',
    decision: row.decision ?? '',
    budgetLineId: row.budget_line_id ?? null,
    rebaselineOnApproval: row.rebaseline_on_approval === true,
    scopeChange: row.scope_change ?? null,
    approvals: Array.isArray(row.approvals) ? row.approvals : [],
  };
}

export function toChangeRequestRow(
  projectId: string,
  c: ChangeRequest,
): Omit<ChangeRequestRow, never> {
  return {
    id: c.id,
    project_id: projectId,
    title: c.title,
    type: c.type,
    status: c.status,
    requested_by: c.requestedBy ?? null,
    request_date: c.requestDate || null,
    impact: c.impact ?? null,
    cost_impact: c.costImpact ?? 0,
    schedule_impact: c.scheduleImpact ?? 0,
    priority: c.priority ?? 'medium',
    justification: c.justification ?? null,
    decision: c.decision ?? null,
    // Phase 30.10: empty string never makes it to the DB (FK is `text` and the
    // valid values are non-empty IDs; coerce to null so the FK constraint
    // accepts "no link").
    budget_line_id: c.budgetLineId && c.budgetLineId.length > 0 ? c.budgetLineId : null,
    rebaseline_on_approval: c.rebaselineOnApproval ?? false,
    scope_change: c.scopeChange ?? null,
    approvals: c.approvals ?? [],
  };
}

// ---------------------------------------------------------------------------
// Decisions (Migration 0055) — mirror change_requests, no approval workflow
// ---------------------------------------------------------------------------

export interface DecisionRow {
  id: string;
  project_id: string;
  title: string;
  context: string | null;
  decision: string | null;
  category: string | null;
  status: string;
  decided_by: string | null;
  decision_date: string | null;
  flagged_for_steering: boolean;
  created_at?: string;
  updated_at?: string;
}

export function fromDecisionRow(row: DecisionRow): Decision {
  return {
    id: row.id,
    title: row.title,
    context: row.context ?? undefined,
    decision: row.decision ?? '',
    category: row.category ?? undefined,
    status: (row.status as Decision['status']) ?? 'decided',
    decidedBy: row.decided_by ?? '',
    decisionDate: row.decision_date ?? '',
    flaggedForSteering: row.flagged_for_steering ?? false,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function toDecisionRow(
  projectId: string,
  d: Decision,
): Omit<DecisionRow, 'created_at' | 'updated_at'> {
  return {
    id: d.id,
    project_id: projectId,
    title: d.title,
    context: d.context ?? null,
    decision: d.decision ?? null,
    category: d.category ?? null,
    status: d.status ?? 'decided',
    decided_by: d.decidedBy ?? null,
    decision_date: d.decisionDate || null,
    flagged_for_steering: d.flaggedForSteering ?? false,
  };
}

// ---------------------------------------------------------------------------
// Action items (Migration 0056) — mirror decisions, lightweight follow-ups
// ---------------------------------------------------------------------------

export interface ActionItemRow {
  id: string;
  project_id: string;
  title: string;
  description: string | null;
  owner: string | null;
  due_date: string | null;
  status: string;
  flagged_for_steering: boolean;
  created_at?: string;
  updated_at?: string;
}

export function fromActionItemRow(row: ActionItemRow): ActionItem {
  return {
    id: row.id,
    title: row.title,
    description: row.description ?? undefined,
    owner: row.owner ?? '',
    dueDate: row.due_date ?? undefined,
    status: (row.status as ActionItem['status']) ?? 'open',
    flaggedForSteering: row.flagged_for_steering ?? false,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function toActionItemRow(
  projectId: string,
  a: ActionItem,
): Omit<ActionItemRow, 'created_at' | 'updated_at'> {
  return {
    id: a.id,
    project_id: projectId,
    title: a.title,
    description: a.description ?? null,
    owner: a.owner ?? null,
    due_date: a.dueDate || null,
    status: a.status ?? 'open',
    flagged_for_steering: a.flaggedForSteering ?? false,
  };
}

// ---------------------------------------------------------------------------
// Baselines (snapshot kept as JSONB)
// ---------------------------------------------------------------------------

export interface BaselineRow {
  id: string;
  project_id: string;
  name: string;
  type: string;
  set_date: string;
  set_by: string | null;
  reason: string | null;
  linked_change_request: string | null;
  approved_by: string | null;
  status: string;
  snapshot: unknown;
}

export function fromBaselineRow(row: BaselineRow): Baseline {
  return {
    id: row.id,
    name: row.name,
    type: row.type as Baseline['type'],
    setDate: row.set_date,
    setBy: row.set_by ?? '',
    reason: row.reason ?? '',
    linkedChangeRequest: row.linked_change_request,
    approvedBy: row.approved_by ?? '',
    status: row.status as Baseline['status'],
    snapshot: (row.snapshot as Baseline['snapshot']) ?? {
      projectId: row.project_id,
      startDate: '',
      endDate: '',
      budget: 0,
      wbsTasks: [],
    },
  };
}

export function toBaselineRow(projectId: string, b: Baseline): Omit<BaselineRow, never> {
  return {
    id: b.id,
    project_id: projectId,
    name: b.name,
    type: b.type,
    set_date: b.setDate,
    set_by: b.setBy ?? null,
    reason: b.reason ?? null,
    linked_change_request: b.linkedChangeRequest ?? null,
    approved_by: b.approvedBy ?? null,
    status: b.status,
    snapshot: b.snapshot,
  };
}

// ---------------------------------------------------------------------------
// Phase 30.9 (Budget B2) — BudgetLine row shape + converters
// ---------------------------------------------------------------------------

/** Raw row shape from supabase.from('budget_lines').select(*) */
export interface BudgetLineRow {
  id: string;
  project_id: string;
  /** Phase 30.12: which version of the project's budget this line belongs to. */
  budget_version_id: string;
  category: string;
  description: string;
  planned_amount: number | string; // numeric → string in some drivers
  actual_amount: number | string;
  cost_type: string | null;
  is_labor?: boolean | null;
  created_at: string;
  updated_at: string;
}

export function fromBudgetLineRow(row: BudgetLineRow): BudgetLine {
  return {
    id: row.id,
    budgetVersionId: row.budget_version_id,
    category: row.category ?? '',
    description: row.description ?? '',
    plannedAmount: Number(row.planned_amount ?? 0),
    actualAmount: Number(row.actual_amount ?? 0),
    costType: row.cost_type === 'capital' || row.cost_type === 'expense' ? row.cost_type : undefined,
    isLabor: row.is_labor === true,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function toBudgetLineRow(projectId: string, line: BudgetLine) {
  // We deliberately don't include created_at/updated_at — the server defaults
  // populate them on insert, and the trigger keeps updated_at fresh on update.
  // Phase 30.12: budget_version_id is part of the PK and must be sent.
  return {
    id: line.id,
    project_id: projectId,
    budget_version_id: line.budgetVersionId,
    category: line.category ?? '',
    description: line.description ?? '',
    planned_amount: line.plannedAmount ?? 0,
    actual_amount: line.actualAmount ?? 0,
    cost_type: line.costType ?? null,
    is_labor: line.isLabor ?? false,
  };
}

// ---------------------------------------------------------------------------
// Phase 30.12 (Budget B3) — BudgetVersion row shape + converters
// ---------------------------------------------------------------------------

export interface BudgetVersionRow {
  id: string;
  project_id: string;
  name: string;
  is_default: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export function fromBudgetVersionRow(row: BudgetVersionRow): BudgetVersion {
  return {
    id: row.id,
    name: row.name ?? '',
    isDefault: row.is_default ?? false,
    createdBy: row.created_by ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function toBudgetVersionRow(
  projectId: string,
  version: BudgetVersion,
  createdBy?: string | null,
) {
  // created_by is only set on insert; subsequent updates leave it alone.
  return {
    id: version.id,
    project_id: projectId,
    name: version.name ?? '',
    is_default: version.isDefault ?? false,
    ...(createdBy !== undefined ? { created_by: createdBy } : {}),
  };
}

// ---------------------------------------------------------------------------
// Phase 30.15 (Budget B4) — BudgetLinePeriod row shape + converters
// ---------------------------------------------------------------------------

export interface BudgetLinePeriodRow {
  project_id: string;
  budget_version_id: string;
  budget_line_id: string;
  period_start: string;          // YYYY-MM-DD; always first of month
  planned_amount: number | string;
  actual_amount: number | string;
  created_at: string;
  updated_at: string;
}

export function fromBudgetLinePeriodRow(row: BudgetLinePeriodRow): BudgetLinePeriod {
  return {
    periodStart: row.period_start,
    budgetLineId: row.budget_line_id,
    budgetVersionId: row.budget_version_id,
    plannedAmount: Number(row.planned_amount ?? 0),
    actualAmount: Number(row.actual_amount ?? 0),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function toBudgetLinePeriodRow(
  projectId: string,
  period: BudgetLinePeriod,
) {
  return {
    project_id: projectId,
    budget_version_id: period.budgetVersionId,
    budget_line_id: period.budgetLineId,
    period_start: period.periodStart,
    planned_amount: period.plannedAmount ?? 0,
    actual_amount: period.actualAmount ?? 0,
  };
}
